import React, { useState } from 'react';
import findKey from 'lodash/findKey';
import { Box, Dialog, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import {NOTIFICATION_TYPE, showNotificationBar} from '../../../actions/error-pages/NotificationBarActions';
import { getUserId, isUserAdmin } from '../../../actions/profile/UserActions';
import { LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { updateSiteSubmission } from '../../../actions/internal/SiteActions';
import { SUBMISSION_STATUS, SUBMISSION_STATUS_LOOKUP } from '../../../constants/Statuses';
import { StyledMultilineTextField, StyledRichTextbox, StyledTextField } from '../../common/StyledTextField';
import { StyledDropDown } from '../../common/StyledDropDown';
import StyledSwitch from '../../common/StyledSwitch';
import { getAllTeamMembers } from '../../../selectors/AdminSelectors';

const EditRequestModal = ({ open, onClose, onSave, request, match }) => {

  const [ status, setStatus ] = useState();
  const [ notes, setNotes ] = useState();
  const [ assignedTo, setAssignedTo ] = useState();
  const [ values, setValues ] = useState({})
  const [errors, setErrors] = useState([]);
  const [ sendNotification, setSendNotification ] = useState(true);

  const [ isLoading, setIsLoading ] = useState(false);

  const teamMembers = getAllTeamMembers();

  const setFieldValue = (id, value) => {
    setValues({ ...values, [id]: value })
  }

  const clearForm = () => {
    setStatus();
    setValues({});
    setErrors([]);
    setSendNotification(true);
    setNotes(undefined);
    setAssignedTo();
  }

  const handleClose = () => {
    clearForm();
    onClose();
  }

  const validateFields = () => {
    return Object.keys(values).filter(key => !values[key] || values[key].trim() === '');
  }

  const handleUpdateRequest = async () => {
    try {
      if (!values && !status) { handleClose(); return; }
      setIsLoading(true)

      // validate payload
      const errors = validateFields();
      if (errors.length > 0) {
        setErrors(errors)
        return;
      }

      // send payload to update request
      const masks = ['submission_fields', 'submission_notes', 'submission_assigned_to']
      const payload = {
        submission_fields: { ...request.submission_fields, ...values },
        submission_notes: notes || null,
        submission_assigned_to: assignedTo === 'UNASSIGNED' || !assignedTo ? null : assignedTo
      }
      if (status) {
        payload.submission_status = SUBMISSION_STATUS[status]
        masks.push('submission_status')
      }

      const site_id = request.submission_identifiers.site ? request.submission_identifiers.site.site_id : undefined;
      const options = { send_submission_notification: sendNotification };

      await updateSiteSubmission(getUserId(), site_id, request.submission_id, masks, payload, options)
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "This request has been updated successfully!");
      clearForm();
      onSave();
    } catch(ex) {
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "An error has occurred while canceling this request. If this continues to happen, please contact us.");
    } finally {
      setIsLoading(false)
    }
  }

  const { submission_status, submission_fields, submission_notes, submission_assigned_to } = request || {}
  const defaultFieldValues = submission_fields || {}
  const isAdmin = isUserAdmin();

  return (
    <>
      <Dialog
          fullWidth
          open={open}
          maxWidth={'md'}
      >
          <DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box>
                <h4 style={{ margin: 0, padding: 0 }}>Edit Request</h4>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              {isAdmin && <StyledDropDown
                label="Status"
                value={status || findKey(SUBMISSION_STATUS, (o) => o === submission_status)}
                onChange={(e) => setStatus(e.target.value)}
              >
                {Object.keys(SUBMISSION_STATUS).map(key => (
                  <MenuItem key={key} value={key}>{SUBMISSION_STATUS_LOOKUP[SUBMISSION_STATUS[key]].label}</MenuItem>
                ))}
              </StyledDropDown> }
              {isAdmin && <StyledDropDown
                label="Assigned To"
                value={assignedTo || submission_assigned_to}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                <MenuItem defaultValue key={'UNASSIGNED'} value={'UNASSIGNED'}><em>Unassigned</em></MenuItem>
                {teamMembers && (teamMembers).map(member => (
                  <MenuItem key={member.user_id} value={member.user_id}>{member.team_member_details.first_name} {member.team_member_details.last_name}</MenuItem>
                ))}
              </StyledDropDown> }
              <StyledTextField
                label="Title"
                onChange={(e) => setFieldValue('title', e.target.value)}
                error={errors.includes('title')}
                value={values['title'] || defaultFieldValues['title'] || ''}
              />
              <StyledMultilineTextField
                label="Description"
                onChange={(e) => setFieldValue('description', e.target.value)}
                error={errors.includes('description')}
                value={values['description'] || defaultFieldValues['description'] || ''}
              />
             
            </Box>
            <h4 style={{ margin: 0, fontSize: '18px', marginTop: '18px', marginBottom: '5px' }}>Internal Notes</h4>
            <StyledRichTextbox
              value={notes || submission_notes}
              onChange={setNotes}
            />
            
          </DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px'}}>
            <Box>
              {status && <StyledSwitch
                  title="Send Notification"
                  onChange={(e) => { setSendNotification(e.target.checked) }}
                  checked={sendNotification}
              />}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
              <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
              <LoadingButton onClick={handleUpdateRequest} isLoading={isLoading}>Save</LoadingButton>
            </Box>
          </Box>
      </Dialog>
    </>
  )
}

export default observer(withRouter(EditRequestModal));

/*
  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', padding: '16px'}}>
    <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
    <LoadingButton onClick={handleUpdateRequest} isLoading={isLoading}>Save</LoadingButton>
  </Box>
*/
